import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { descargarCsv } from '../csv';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Filtro,
  Pastilla,
  Tarjeta,
  Vacio,
} from '../componentes';
import { diaDeLima, soles } from '../formato';
import { semanaAnteriorA, semanaDe } from '../semana';

/**
 * El food cost real de la semana, contra el que dicen las recetas.
 *
 * **Es el número por el que el dueño paga un almacén.** Las recetas dicen
 * cuánto *debería* costar lo vendido; esto dice cuánto *costó*: lo que salió
 * por las ventas, más las mermas, más lo que el conteo encontró de menos. La
 * diferencia es la plata que se fue sin que ninguna receta la explique.
 *
 * Por semana, de lunes a domingo: es como se compra y como se decide. Y con
 * la anterior a un toque, porque un número solo no dice si va bien o mal.
 */
export function CostoReal() {
  const [semana, setSemana] = useState<'esta' | 'anterior'>('esta');
  const rango = semana === 'esta' ? semanaDe(diaDeLima()) : semanaAnteriorA(diaDeLima());

  const consulta = useQuery({
    queryKey: ['costo-real', rango.desde, rango.hasta],
    queryFn: () => almacen.foodCost(rango.desde, rango.hasta),
  });

  const datos = consulta.data;
  const conDiferencia = (datos?.lines ?? []).filter(
    (line) => Number.parseFloat(line.difference) !== 0,
  );

  return (
    <>
      <Cabecera
        titulo="Costo real"
        subtitulo="El food cost de verdad, contra el que dicen las recetas"
        ayuda="Lo que salió del almacén por las ventas —según receta, más mermas, más lo que el conteo encontró de menos— sobre lo vendido sin IGV. La comida del personal va aparte: no es venta."
        acciones={
          <>
            <div className="filtros">
              <Filtro texto="Esta semana" activo={semana === 'esta'} onClick={() => setSemana('esta')} />
              <Filtro
                texto="La anterior"
                activo={semana === 'anterior'}
                onClick={() => setSemana('anterior')}
              />
            </div>
            <Boton
              onClick={() =>
                descargarCsv(
                  `costo-real-${rango.desde}-a-${rango.hasta}.csv`,
                  ['Insumo', 'Unidad', 'Según receta (S/)', 'Real (S/)', 'Diferencia (S/)'],
                  (datos?.lines ?? []).map((line) => [
                    line.supply,
                    line.unit,
                    line.theoretical,
                    line.real,
                    line.difference,
                  ]),
                )
              }
              disabled={(datos?.lines ?? []).length === 0}
            >
              Exportar
            </Boton>
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading || datos === undefined ? (
          <Cargando que="el costo de la semana" />
        ) : (
          <>
            <p className="nota">
              Del {rango.desde.split('-').reverse().join('/')} al{' '}
              {rango.hasta.split('-').reverse().join('/')} · vendido sin IGV:{' '}
              <strong>{soles(datos.netSales)}</strong>
            </p>

            {!datos.wasCounted ? (
              <p className="aviso-caja aviso-caja--aviso">
                No se cerró ningún conteo esta semana: lo que se fue sin que nadie lo apuntara
                solo aparece contando, así que el costo real de aquí se queda corto.
              </p>
            ) : null}

            <div className="cifras">
              <Tarjeta>
                <p className="versal">Según las recetas</p>
                <p className="cifra__valor">{porcentajeDe(datos.theoreticalPercentage)}</p>
                <p className="cifra__apoyo">{soles(datos.theoretical)} en ingredientes de lo vendido</p>
              </Tarjeta>
              <Tarjeta>
                <p className="versal">Real</p>
                <p className="cifra__valor">{porcentajeDe(datos.realPercentage)}</p>
                <p className="cifra__apoyo">
                  {soles(datos.real)}: receta, más {soles(datos.waste)} de mermas, más{' '}
                  {soles(datos.countAdjustments)} que encontró el conteo
                </p>
              </Tarjeta>
              <Tarjeta>
                <p className="versal">Se fue sin receta</p>
                <p
                  className={`cifra__valor${
                    Number.parseFloat(datos.real) > Number.parseFloat(datos.theoretical)
                      ? ' negativo'
                      : ''
                  }`}
                >
                  {soles(
                    (Number.parseFloat(datos.real) - Number.parseFloat(datos.theoretical)).toFixed(2),
                  )}
                </p>
                <p className="cifra__apoyo">
                  Porciones más grandes, mermas, robo o un gramaje mal cargado.
                </p>
              </Tarjeta>
              <Tarjeta>
                <p className="versal">Comida del personal</p>
                <p className="cifra__valor">{soles(datos.staffMeals)}</p>
                <p className="cifra__apoyo">Aparte: no es venta y no cuenta en el porcentaje.</p>
              </Tarjeta>
              {/*
               * Lo regalado al cliente se cocinó con receta y no se cobró:
               * metido en «según receta» subía el porcentaje sin que se fuera
               * nada de más. Un servidor de antes no lo manda y no sale.
               */}
              {datos.courtesies !== undefined ? (
                <Tarjeta>
                  <p className="versal">Cortesías</p>
                  <p className="cifra__valor">{soles(datos.courtesies)}</p>
                  <p className="cifra__apoyo">Lo regalado al cliente: aparte, como la comida del personal.</p>
                </Tarjeta>
              ) : null}
            </div>

            {conDiferencia.length === 0 ? (
              <Vacio
                titulo="Lo que salió es lo que dicen las recetas"
                mensaje="Ningún insumo se fue de más esta semana. Si además se contó, es la semana que se busca."
              />
            ) : (
              <div className="tabla-envoltura">
                <table>
                  <thead>
                    <tr>
                      <th>Insumo</th>
                      <th className="numero">
                        <ConAyuda ayuda="Lo que las ventas descontaron según la receta, al costo de ese momento.">
                          Según receta
                        </ConAyuda>
                      </th>
                      <th className="numero">
                        <ConAyuda ayuda="Lo que de verdad salió: receta, mermas y lo que el conteo encontró de menos.">
                          Real
                        </ConAyuda>
                      </th>
                      <th className="numero">
                        <ConAyuda ayuda="Lo que se fue sin receta que lo explique. Negativo: el conteo encontró de más, una entrada sin registrar.">
                          Diferencia
                        </ConAyuda>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {conDiferencia.map((line) => (
                      <tr key={line.supplyId}>
                        <td>
                          <strong>{line.supply}</strong> <span className="apagado">{line.unit}</span>
                        </td>
                        <td className="numero mono apagado">{soles(line.theoretical)}</td>
                        <td className="numero mono">{soles(line.real)}</td>
                        <td className="numero">
                          {Number.parseFloat(line.difference) > 0 ? (
                            <strong className="negativo">{soles(line.difference)}</strong>
                          ) : (
                            <Pastilla texto={`${soles(line.difference)} de más`} tono="aviso" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** «31,4 %», o un guion sin ventas: no hay de qué sacar el porcentaje. */
function porcentajeDe(valor: number | null): string {
  return valor === null ? '—' : `${valor.toFixed(1).replace('.', ',')} %`;
}
